import { useEffect, useRef } from 'react';

const NON_TEXT_INPUTS = new Set([
    'checkbox',
    'radio',
    'button',
    'submit',
    'reset',
]);
const TEXT_ROLES = '[role="textbox"],[role="combobox"],[role="searchbox"]';

/** True when a key press is going into a box the person types in (the browser owns those keys). */
export function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    const tag = target.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (tag === 'INPUT')
        return !NON_TEXT_INPUTS.has((target as HTMLInputElement).type);
    const editable = target
        .closest('[contenteditable]')
        ?.getAttribute('contenteditable');
    if (editable !== undefined && editable !== null && editable !== 'false')
        return true;
    return target.closest(TEXT_ROLES) !== null;
}

const inDialog = (target: EventTarget | null) =>
    target instanceof Element &&
    target.closest('[role="dialog"],[role="alertdialog"]') !== null;

/** Arrows step through splits only from the page itself or the editor; elsewhere they keep scrolling and moving focus. */
const arrowsAreFree = (target: EventTarget | null) =>
    target === document.body ||
    (target instanceof Element &&
        target.closest('[data-testid="PageSplitEditor"]') !== null);

export interface SplitShortcutActions {
    /** Returns whether there was something to undo. */
    undo: () => boolean;
    previousPage: () => void;
    nextPage: () => void;
    splitHere: () => void;
    verify: () => void;
    useAsStart: () => void;
    useAsEnd: () => void;
    previousSplit: () => void;
    nextSplit: () => void;
    save: () => void;
    /** Absent when the person did not come from a list, so N does nothing. */
    nextFile?: () => void;
    /** Absent when there is no earlier file in that list, so P does nothing. */
    previousFile?: () => void;
}

export const SHORTCUT_HELP: {
    what: string;
    keys: string;
    needsFileList?: boolean;
}[] = [
    { what: 'Next / previous page', keys: 'J  K' },
    { what: 'Next / previous split', keys: '↓  ↑' },
    { what: 'Split here at the current page', keys: 'S' },
    { what: 'Set start to the current page', keys: '[' },
    { what: 'Set end to the current page', keys: ']' },
    { what: 'Verify the open split', keys: 'V' },
    { what: 'Save the splits', keys: '⌘S / Ctrl+S' },
    { what: 'Undo the last split change', keys: '⌘Z / Ctrl+Z' },
    {
        what: 'Next / previous file in the list you came from',
        keys: 'N  P',
        needsFileList: true,
    },
];

/** The split editor's keyboard shortcuts; every one but save stays quiet while focus is in a text box, and all stay quiet while a dialog is open. */
export function useSplitShortcuts(actions: SplitShortcutActions) {
    const actionsRef = useRef(actions);
    actionsRef.current = actions;

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented || inDialog(event.target)) return;
            const current = actionsRef.current;
            const typing = isTypingTarget(event.target);
            if ((event.metaKey || event.ctrlKey) && !event.altKey) {
                const chord = event.key.toLowerCase();
                if (chord === 's' && !event.shiftKey) {
                    // Saving works from inside a box too: the browser's own Save dialog is never wanted here.
                    event.preventDefault();
                    current.save();
                } else if (
                    chord === 'z' &&
                    !event.shiftKey &&
                    !typing &&
                    current.undo()
                ) {
                    event.preventDefault();
                }
                return;
            }
            if (typing) return;
            if (event.altKey) return;
            const letterActions: Record<string, () => void> = {
                j: current.nextPage,
                k: current.previousPage,
                s: current.splitHere,
                v: current.verify,
                '[': current.useAsStart,
                ']': current.useAsEnd,
                arrowdown: current.nextSplit,
                arrowup: current.previousSplit,
                ...(current.nextFile ? { n: current.nextFile } : {}),
                ...(current.previousFile ? { p: current.previousFile } : {}),
            };
            const run = letterActions[event.key.toLowerCase()];
            if (!run) return;
            if (event.key.startsWith('Arrow') && !arrowsAreFree(event.target))
                return;
            event.preventDefault();
            run();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);
}
